import {
  createApiRef,
  discoveryApiRef,
  fetchApiRef,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';

// ---- Shared alert contract (mirrors security-backend types) ----------------

export type AlertTool = 'dependabot' | 'code-scanning';
export type AlertSeverity =
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'error'
  | 'warning'
  | 'note'
  | 'unknown';
export type AlertState =
  'open' | 'dismissed' | 'fixed' | 'auto_dismissed' | 'unknown';

export interface SecurityAlert {
  id: string;
  tool: AlertTool;
  severity: AlertSeverity;
  state: AlertState;
  title: string;
  description?: string;
  htmlUrl: string;
  createdAt: string;
  updatedAt?: string;
  fixedAt?: string | null;
  dismissedAt?: string | null;
  packageName?: string;
  packageEcosystem?: string;
  cvss?: number;
  ruleId?: string;
  toolName?: string;
}

export interface AlertsSummary {
  total: number;
  byTool: Partial<Record<AlertTool, number>>;
  bySeverity: Partial<Record<string, number>>;
}

export interface AlertsResponse {
  alerts: SecurityAlert[];
  summary: AlertsSummary;
}

export const securityBackendApiRef = createApiRef<SecurityBackendApi>({
  id: 'plugin.github-widgets.security-backend',
});

export interface SecurityBackendApi {
  getAlerts(opts: {
    owner: string;
    repo: string;
    tool?: AlertTool | 'all';
    severity?: string;
    state?: AlertState | 'all';
  }): Promise<AlertsResponse>;
}

class SecurityBackendClient implements SecurityBackendApi {
  constructor(
    private discoveryApi: { getBaseUrl(pluginId: string): Promise<string> },
    private fetchApi: { fetch: typeof fetch },
  ) {}

  async getAlerts(opts: {
    owner: string;
    repo: string;
    tool?: AlertTool | 'all';
    severity?: string;
    state?: AlertState | 'all';
  }): Promise<AlertsResponse> {
    const base = await this.discoveryApi.getBaseUrl('security-backend');
    const params = new URLSearchParams({ owner: opts.owner, repo: opts.repo });
    if (opts.tool && opts.tool !== 'all') params.set('tool', opts.tool);
    if (opts.severity) params.set('severity', opts.severity);
    if (opts.state && opts.state !== 'all') params.set('state', opts.state);

    const res = await this.fetchApi.fetch(`${base}/v1/alerts?${params}`);
    if (!res.ok) {
      throw new Error(
        `Security backend request failed: ${res.status} ${res.statusText}`,
      );
    }
    return res.json();
  }
}

export const securityBackendApiExtension = ApiBlueprint.make({
  name: 'security-backend',
  params: defineParams =>
    defineParams({
      api: securityBackendApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new SecurityBackendClient(discoveryApi, fetchApi),
    }),
});
